import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Clock, Languages, PhoneCall, Send } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";

const ContactSection = () => {
  const { t, lang } = useLanguage();
  const c = t.contact;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    pflegedienst: "", city: "", care_type: "", bottleneck: "", message: "",
    name: "", phone: "", email: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handler = () => setStep(0);
    window.addEventListener("joudi-select-service", handler);
    return () => window.removeEventListener("joudi-select-service", handler);
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const validateStep = () => {
    const errs = {};
    if (step === 0) {
      if (form.pflegedienst.trim().length < 2) errs.pflegedienst = c.pflegedienstRequired;
      if (form.city.trim().length < 2) errs.city = c.cityRequired;
      if (!form.care_type) errs.care_type = c.careTypeRequired;
    }
    if (step === 1) {
      if (!form.bottleneck) errs.bottleneck = c.bottleneckRequired;
      if (form.message.trim().length < 10) errs.message = c.messageRequired;
    }
    if (step === 2) {
      if (form.name.trim().length < 2) errs.name = c.nameRequired;
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errs.email = c.emailRequired;
      if (form.phone.trim().length < 6) errs.phone = c.phoneRequired;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = (e) => {
    e && e.preventDefault();
    if (!validateStep()) return;
    if (step < 2) setStep(step + 1);
  };
  const back = () => {
    setErrors({});
    setStep(Math.max(0, step - 1));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      toast.success(c.successToast, { data: { testid: "contact-success-toast" } });
      setForm({ pflegedienst: "", city: "", care_type: "", bottleneck: "", message: "", name: "", phone: "", email: "" });
      setErrors({});
      setStep(0);
    } catch (err) {
      toast.error(c.errorToast, { data: { testid: "contact-error-toast" } });
    } finally {
      setSending(false);
    }
  };

  const inputCls = (err) =>
    `w-full rounded-[3px] border bg-dark px-4 py-3 text-sm text-bone outline-none transition-colors placeholder:text-bone/35 focus:border-gold focus:ring-2 focus:ring-gold/25 ${
      err ? "border-terra" : "border-bone/15"
    }`;

  const sidePoints = [
    { icon: Clock, label: c.points[0] },
    { icon: Languages, label: c.points[1] },
    { icon: CheckCircle2, label: c.points[2] },
  ];

  const summary = [
    [c.pflegedienst, form.pflegedienst],
    [c.city, form.city],
    [c.careType, form.care_type],
    [c.bottleneck, form.bottleneck],
    [c.message, form.message],
  ].filter(([, v]) => v);

  return (
    <section id="kontakt" className="scroll-mt-24 py-20 lg:py-32">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-14 px-6 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>{c.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-display text-4xl font-medium tracking-tight sm:text-5xl lg:text-6xl">{c.title}</h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-inkmuted">{c.sub}</p>
          </Reveal>
          <div className="mt-10 space-y-4">
            {sidePoints.map((p, i) => (
              <Reveal key={p.label} delay={0.2 + i * 0.06}>
                <div className="flex items-center gap-3 text-sm text-bone/75">
                  <span className="inline-flex rounded-full border border-bone/15 p-2 text-gold">
                    <p.icon size={14} />
                  </span>
                  {p.label}
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.45}>
              <a
                href="mailto:jean@joudi-group.com"
                data-testid="contact-sales-person"
                className="inline-flex items-center gap-4 rounded-[3px] border border-bone/10 bg-carddark px-5 py-4 transition-colors hover:border-gold"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gold/15 font-display text-lg font-bold text-gold">J</span>
                <span>
                  <span className="block text-sm font-semibold text-bone">
                    {lang === "de" ? "Jean — Ihr Ansprechpartner für Pflege-Wachstum" : "Jean — your contact for care growth"}
                  </span>
                  <span className="mt-0.5 block text-xs text-inkmuted">jean@joudi-group.com</span>
                </span>
              </a>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form
            onSubmit={step === 2 ? submit : next}
            noValidate
            data-testid="contact-form"
            className="rounded-[3px] border border-bone/10 bg-carddark p-8 lg:p-12"
          >
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
              <h3 className="font-display text-2xl font-medium">{c.formTitle}</h3>
              <p data-testid="contact-step-indicator" className="font-mono text-xs uppercase tracking-[0.2em] text-gold">
                {c.stepOf.replace("{n}", String(step + 1))}
              </p>
            </div>
            <div className="mt-4 flex gap-2" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <span key={i} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${i <= step ? "bg-gold" : "bg-bone/10"}`} />
              ))}
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-inkmuted">{c.steps[step]}</p>

            {step === 0 && (
              <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="cf-pflegedienst" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-inkmuted">{c.pflegedienst} *</label>
                  <input id="cf-pflegedienst" data-testid="contact-pflegedienst-input" value={form.pflegedienst} onChange={set("pflegedienst")} className={inputCls(errors.pflegedienst)} placeholder={c.pflegedienstPlaceholder} />
                  {errors.pflegedienst && <p data-testid="contact-pflegedienst-error" className="mt-2 text-xs text-terra">{errors.pflegedienst}</p>}
                </div>
                <div>
                  <label htmlFor="cf-city" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-inkmuted">{c.city} *</label>
                  <input id="cf-city" data-testid="contact-city-input" value={form.city} onChange={set("city")} className={inputCls(errors.city)} placeholder={c.cityPlaceholder} />
                  {errors.city && <p data-testid="contact-city-error" className="mt-2 text-xs text-terra">{errors.city}</p>}
                </div>
                <div>
                  <label htmlFor="cf-caretype" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-inkmuted">{c.careType} *</label>
                  <select id="cf-caretype" data-testid="contact-caretype-select" value={form.care_type} onChange={set("care_type")} className={`${inputCls(errors.care_type)} ${form.care_type ? "" : "text-bone/40"}`}>
                    <option value="">{c.careTypePlaceholder}</option>
                    {c.careOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  {errors.care_type && <p data-testid="contact-caretype-error" className="mt-2 text-xs text-terra">{errors.care_type}</p>}
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="mt-8">
                <p className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-inkmuted">{c.bottleneck} *</p>
                <div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label={c.bottleneck}>
                  {c.bottleneckOptions.map((o) => (
                    <button
                      key={o}
                      type="button"
                      data-testid={`contact-bottleneck-${form.bottleneck === o ? "selected" : "option"}`}
                      onClick={() => setForm({ ...form, bottleneck: o })}
                      className={`rounded-[3px] border px-4 py-2.5 text-sm transition-colors ${form.bottleneck === o ? "border-gold bg-gold/10 text-bone" : "border-bone/15 text-bone/60 hover:border-bone/40 hover:text-bone"}`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
                {errors.bottleneck && <p data-testid="contact-bottleneck-error" className="mt-2 text-xs text-terra">{errors.bottleneck}</p>}
                <p className="mt-4 text-xs text-inkmuted">{c.bottleneckHint}</p>
                <div className="mt-6">
                  <label htmlFor="cf-message" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-inkmuted">{c.message} *</label>
                  <textarea id="cf-message" data-testid="contact-message-textarea" value={form.message} onChange={set("message")} rows={4} className={inputCls(errors.message)} placeholder={c.messagePlaceholder} />
                  {errors.message && <p data-testid="contact-message-error" className="mt-2 text-xs text-terra">{errors.message}</p>}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="sm:col-span-2 rounded-[3px] border border-bone/10 bg-dark/60 p-5">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-gold">{c.summaryTitle}</p>
                  <dl className="mt-3 space-y-1.5">
                    {summary.map(([k, v]) => (
                      <div key={k} className="flex gap-3 text-sm">
                        <dt className="shrink-0 text-inkmuted">{k}:</dt>
                        <dd data-testid="contact-summary-value" className="truncate text-bone/85">{v}</dd>
                      </div>
                    ))}
                    {!summary.length && <dd className="text-sm text-inkmuted">{c.summaryEmpty}</dd>}
                  </dl>
                </div>
                <div>
                  <label htmlFor="cf-name" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-inkmuted">{c.name} *</label>
                  <input id="cf-name" data-testid="contact-name-input" value={form.name} onChange={set("name")} className={inputCls(errors.name)} placeholder="Max Mustermann" />
                  {errors.name && <p data-testid="contact-name-error" className="mt-2 text-xs text-terra">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="cf-phone" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-inkmuted">{c.phone} *</label>
                  <input id="cf-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={inputCls(errors.phone)} placeholder={c.phonePlaceholder} />
                  {errors.phone && <p data-testid="contact-phone-error" className="mt-2 text-xs text-terra">{errors.phone}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-email" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-inkmuted">{c.email} *</label>
                  <input id="cf-email" type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} className={inputCls(errors.email)} placeholder="name@pflegedienst.de" />
                  {errors.email && <p data-testid="contact-email-error" className="mt-2 text-xs text-terra">{errors.email}</p>}
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-between">
              {step > 0 ? (
                <button
                  type="button"
                  data-testid="contact-back-btn"
                  onClick={back}
                  className="inline-flex items-center justify-center gap-2 rounded-[3px] border border-bone/20 px-6 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-bone transition-colors hover:border-gold hover:text-gold"
                >
                  <ArrowLeft size={15} />
                  {c.back}
                </button>
              ) : (
                <span />
              )}
              {step < 2 ? (
                <button
                  type="submit"
                  data-testid="contact-next-btn"
                  className="group inline-flex items-center justify-center gap-2 rounded-[3px] bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone"
                >
                  {c.next}
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                </button>
              ) : (
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={sending}
                  className="group inline-flex items-center justify-center gap-2 rounded-[3px] bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? c.sending : c.submit}
                  {!sending && <Send size={14} className="transition-transform group-hover:translate-x-0.5" />}
                </button>
              )}
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default ContactSection;
